/* eslint-disable max-len */
import { capitalizeFirstLetter, getQueryParam } from 'helpers/utils';
import { ButtonStyle } from 'common/button';
import { useEffect, useReducer, useState } from 'react';
import {
  signUpinitialState,
  SignUpReducer,
} from 'pages/sign-up/sign-up-reducer';
import { utmProperties } from 'config/constants';
import { goToPage, RouteName } from 'routes';

import { UserController } from 'networking/controllers/user-controller';
import { Breakpoints } from 'common/enums';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { useMediaQuery } from 'hooks/use-media-query';
import { LocalStorageApi } from 'helpers/local-storage';
import { getRouteFor } from 'routes/route-helpers';
import { ModalWarning } from 'common/modal-warning';
import { VaultCard } from './components/vault-card/vault-card';
import styles from './signup-onboarding.module.scss';
import { SignUpForm } from './components/sign-up-form/sign-up-form';

const SignUpOnboarding: React.FC = () => {
  const [state, dispatch] = useReducer(SignUpReducer, signUpinitialState);

  const email = getQueryParam('email');
  const name = getQueryParam('name');
  const token = getQueryParam('token');
  const vaultId = getQueryParam('vault_id');
  const [vaultTitle, setVaultTitle] = useState<string>('');
  const [vaultIcon, setVaultIcon] = useState<string | undefined>('');
  const [vaultOwnerName, setVaultOwnerName] = useState<string>('');

  const isMobile = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);
  const [invalidCouponCode, setInvalidCouponCode] = useState<boolean>(false);

  const showInvalidCodeModal = () => (
    <ModalWarning
      title="Invalid coupon code"
      content={
        <div className={styles.modalSignUpInfo}>
          There’s been an error with the coupon code associated with your
          referral link. Please contact support for more information.
        </div>
      }
      successButtonText="Register without coupon"
      closeFn={() => goToPage(RouteName.SignIn)}
      successFn={() => setInvalidCouponCode(false)}
      disableManualClosing
      successStyleButton={ButtonStyle.PrimaryFilled}
    />
  );

  useEffect(() => {
    const values = { email, name };
    Object.entries(values).forEach(([field, value]) => {
      if (value) {
        dispatch({
          type: 'CHANGE_INPUT',
          object: 'user',
          field,
          value: value || '',
        });
      }
    });

    const companySingUp = getQueryParam('companyName');
    if (companySingUp) {
      dispatch({
        type: 'CHANGE_INPUT',
        object: 'user',
        field: 'companyName',
        value: capitalizeFirstLetter(companySingUp),
      });
    }

    if (token) {
      dispatch({ type: 'ADD_TOKEN', token });
    }

    utmProperties.forEach((utmProperty: UtmProperties) => {
      const utmValue = getQueryParam(utmProperty);
      if (utmValue) {
        dispatch({
          type: 'SET_UTM_PROPERTY',
          utmProperty: UserController.deSerializeUtmProperty(
            utmProperty,
          ) as string,
          utmValue,
        });
      }
    });

    const vaultIdParam = getQueryParam('vault_id');
    const tab = getQueryParam('tab');
    if (vaultIdParam && tab) {
      const vaultUrl = getRouteFor(
        RouteName.VaultDetail,
        {
          id: vaultIdParam,
        },
        { tab },
      );
      LocalStorageApi.set('previous-url', vaultUrl);
    }

    return () => {
      dispatch({ type: 'REGISTER_SUCCESS' });
    };
  }, []);

  const fetchVault = async () => {
    const response = await VaultsController.getVaultForInvite(
      Number(vaultId),
      token || '',
    );
    setVaultTitle(response.title);
    setVaultIcon(response.coverUrl);
    setVaultOwnerName(response.owner.name);
  };

  useEffect(() => {
    fetchVault();
  }, [token]);

  return (
    <div className={styles.containerVault}>
      {invalidCouponCode && showInvalidCodeModal()}
      {!isMobile && (
        <VaultCard
          image={vaultIcon}
          name={vaultTitle}
          subtitle="A life story"
        />
      )}
      <div className={styles.container}>
        <SignUpForm
          user={state.user}
          dispatch={dispatch}
          couponsCodeApplied={state.couponCodeApplied}
          token={token || ''}
          vaultOwner={vaultOwnerName}
          vaultId={vaultId || ''}
        />
      </div>
    </div>
  );
};

export { SignUpOnboarding };
